import React from "react";
import { DndContext, closestCenter } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import SortableColumn from "./SortableColumn";
import ButtonComponent from "@components/ButtonComponent";
import { helperTitle } from "@helpers/helpers";
import {
  ZERO_VALUE,
  POPUP_CONTAINER_CLASS,
  POPUP_INNER_CLASS,
  COLUMNS_LIST_CLASS,
  ORDERING_LIST_CLASS,
  TOGGLE_ALL_LABEL_CLASS,
  CHECKBOX_CLASS,
  TEST_ID_ORDERING_POPUP,
  TEST_ID_ORDERING_LIST,
  TEST_ID_TOGGLE_ALL,
  TEST_ID_RESET_ORDER,
  BUTTON_SIZE_SM,
  BUTTON_TYPE_CANCEL,
} from "./constants";

/**
 * OrderingPopup Component
 * Displays the column ordering and visibility configuration popup
 * @param {Object} table - Table instance from tanstack/react-table
 * @param {boolean} showResetButton - Whether to show reset button
 * @param {Function} handleReset - Callback for reset action
 * @param {Function} handleToggleAll - Callback for toggle all columns
 * @param {Function} handleDragEnd - Callback for drag end event
 * @param {Function} saveOrderingConfig - Callback to save configuration
 * @param {string[]} allLeafColumns - Array of all leaf column IDs
 * @param {Function} translate - Translation function
 */
const OrderingPopup = ({
  table,
  showResetButton,
  handleReset,
  handleToggleAll,
  handleDragEnd,
  saveOrderingConfig,
  allLeafColumns,
  translate,
}) => {
  const hiddenColumns =
    table.getAllLeafColumns().length - table.getVisibleLeafColumns().length;

  return (
    <div className={POPUP_CONTAINER_CLASS}>
      <div data-testid={TEST_ID_ORDERING_POPUP} className={POPUP_INNER_CLASS}>
        <div className={COLUMNS_LIST_CLASS}>
          <div
            data-testid={TEST_ID_ORDERING_LIST}
            className={ORDERING_LIST_CLASS}
          >
            <div className="p-1 ml-7">
              <label className={TOGGLE_ALL_LABEL_CLASS}>
                <input
                  data-testid={TEST_ID_TOGGLE_ALL}
                  type="checkbox"
                  checked={table.getIsAllColumnsVisible()}
                  onChange={handleToggleAll}
                  className={CHECKBOX_CLASS}
                />
                <span className="cursor-pointer text-xs">
                  {translate("shared.ordering-toggle-all")}
                </span>
              </label>
            </div>
            <DndContext
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={allLeafColumns}
                strategy={verticalListSortingStrategy}
              >
                {table
                  .getAllLeafColumns()
                  .filter((column) => !column.getIsPinned())
                  .map((column) => {
                    return (
                      <SortableColumn
                        key={column.id}
                        id={column.id}
                        header={column.columnDef.header}
                        column={column}
                        saveOrderingConfig={saveOrderingConfig}
                      />
                    );
                  })}
              </SortableContext>
            </DndContext>
          </div>
          {(showResetButton || hiddenColumns > ZERO_VALUE) && (
            <ButtonComponent
              data-testid={TEST_ID_RESET_ORDER}
              id={TEST_ID_RESET_ORDER}
              onClick={handleReset}
              ref={null}
              icon={null}
              text={helperTitle(translate("shared.reset-order"))}
              type={BUTTON_TYPE_CANCEL}
              size={BUTTON_SIZE_SM}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default OrderingPopup;
